import { apiConfig } from './api/apiConfig';

export interface ClauseComment {
  _id: string;
  text: string;
  author: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt?: string;
  isEdited?: boolean;
}

export interface ClauseDecision {
  _id: string;
  contractId: string;
  analysisId: string;
  clauseReference: string;
  status: 'pending' | 'agreed' | 'rejected' | 'compromise';
  decisionMadeBy: string;
  decisionDate: string;
  notes?: string;
  previousStatus?: 'pending' | 'agreed' | 'rejected' | 'compromise';
  comments: ClauseComment[];
  statusHistory: Array<{
    status: 'pending' | 'agreed' | 'rejected' | 'compromise';
    changedBy: string;
    changedAt: string;
    notes?: string;
  }>;
  decisionMaker?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClauseDecisionResponse {
  success: boolean;
  message?: string;
  data?: ClauseDecision | ClauseDecision[] | null;
}

export interface UpdateClauseDecisionRequest {
  status: 'pending' | 'agreed' | 'rejected' | 'compromise';
  notes?: string;
}

class ClauseDecisionService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Get all clause decisions for a contract analysis
   */
  async getClauseDecisions(contractId: string, analysisId: string): Promise<ClauseDecisionResponse> {
    const response = await fetch(
      `${apiConfig.baseURL}/api/contracts/${contractId}/analysis/${analysisId}/clauses/decisions`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch clause decisions');
    }

    return data;
  }

  /**
   * Get a specific clause decision
   */
  async getClauseDecision(
    contractId: string,
    analysisId: string,
    clauseReference: string
  ): Promise<ClauseDecisionResponse> {
    const response = await fetch(
      `${apiConfig.baseURL}/api/contracts/${contractId}/analysis/${analysisId}/clauses/${clauseReference}/decision`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch clause decision');
    }

    return data;
  }

  /**
   * Update or create a clause decision
   */
  async updateClauseDecision(
    contractId: string,
    analysisId: string,
    clauseReference: string,
    updateData: UpdateClauseDecisionRequest
  ): Promise<ClauseDecisionResponse> {
    const response = await fetch(
      `${apiConfig.baseURL}/api/contracts/${contractId}/analysis/${analysisId}/clauses/${clauseReference}/decision`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update clause decision');
    }

    return data;
  }

  /**
   * Add a comment to a clause decision
   */
  async addClauseComment(
    contractId: string,
    analysisId: string,
    clauseReference: string,
    text: string
  ): Promise<ClauseDecisionResponse> {
    const response = await fetch(
      `${apiConfig.baseURL}/api/contracts/${contractId}/analysis/${analysisId}/clauses/${clauseReference}/comments`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ text }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to add comment');
    }

    return data;
  }

  /**
   * Batch update multiple clause decisions
   */
  async batchUpdateClauseDecisions(
    contractId: string,
    analysisId: string,
    decisions: Array<{
      clauseReference: string;
      status: 'pending' | 'agreed' | 'rejected' | 'compromise';
      notes?: string;
    }>
  ): Promise<ClauseDecisionResponse[]> {
    const promises = decisions.map(decision =>
      this.updateClauseDecision(
        contractId,
        analysisId,
        decision.clauseReference,
        {
          status: decision.status,
          notes: decision.notes
        }
      )
    );

    return Promise.all(promises);
  }

  /**
   * Get decisions summary for a contract analysis
   */
  async getDecisionsSummary(contractId: string, analysisId: string): Promise<{
    total: number;
    pending: number;
    agreed: number;
    rejected: number;
    compromise: number;
  }> {
    const response = await this.getClauseDecisions(contractId, analysisId);
    const decisions = (response.data as ClauseDecision[]) || [];

    return {
      total: decisions.length,
      pending: decisions.filter(d => d.status === 'pending').length,
      agreed: decisions.filter(d => d.status === 'agreed').length,
      rejected: decisions.filter(d => d.status === 'rejected').length,
      compromise: decisions.filter(d => d.status === 'compromise').length,
    };
  }
}

export const clauseDecisionService = new ClauseDecisionService();
export default clauseDecisionService;